import { ChevronDown, Loader2 } from "lucide-react";
import { SelectHTMLAttributes, forwardRef, useId } from "react";

interface FetchDropdownProps extends SelectHTMLAttributes<HTMLSelectElement> {
  options: { value: string; label: string }[];
  placeholder?: string;
  status?: "default" | "error";
  defaultOption?: { value: string; label: string };
  errorMessage?: string;
  clearable?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
}

/**
 * Nowadays branded dropdown component.
 *
 * @param props - Native select attributes plus the options, placeholder and error state.
 * @param ref - Forwarded to the underlying `<select>`.
 * @returns The styled select, with its error message under it when `status` is "error".
 */
export const FetchDropdown = forwardRef<HTMLSelectElement, FetchDropdownProps>(
  (
    {
      className = "",
      options,
      placeholder,
      status = "default",
      defaultOption = null,
      errorMessage,
      clearable = false,
      value,
      loading = false,
      leftIcon,
      "aria-describedby": ariaDescribedBy,
      "aria-invalid": ariaInvalid,
      ...props
    },
    ref
  ) => {
    const hasError = status === "error" && !!errorMessage;
    // Announce + associate the error text so screen readers link it to the select.
    const errorId = useId();
    const describedBy = [ariaDescribedBy, hasError ? errorId : undefined].filter(Boolean).join(" ") || undefined;
    const baseClasses = `text-base sm:text-sm font-medium w-full touch-manipulation ${leftIcon ? "pl-8" : "pl-3"} pr-12 py-2 rounded-lg border focus:ring-2 focus:outline-none transition-all duration-200 bg-white text-gray-700 appearance-none`;
    const stateClasses = {
      default:
        "border-gray-200 focus:border-indigo-400 focus:ring-indigo-200 focus:shadow-[0_0_0_3px_rgba(165,180,252,0.2)]",
      error: "border-red-400 focus:border-red-500 focus:ring-red-200",
    };

    const isControlled = value !== undefined;
    const displayValue = isControlled ? value : (defaultOption?.value ?? "");

    return (
      <div className="relative flex flex-col gap-1">
        {leftIcon && (
          <div className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-gray-500">
            {leftIcon}
          </div>
        )}
        <select
          ref={ref}
          id={props.id}
          className={`${baseClasses} ${stateClasses[status]} ${className} ${!displayValue ? "text-gray-400" : ""} ${loading ? "opacity-60 cursor-wait" : ""}`}
          {...(isControlled ? { value: displayValue } : { defaultValue: displayValue })}
          disabled={loading || props.disabled}
          aria-invalid={ariaInvalid ?? (status === "error" ? true : undefined)}
          aria-describedby={describedBy}
          {...props}
        >
          {clearable && <option value="">{placeholder || "Select..."}</option>}
          {placeholder && !clearable && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-1 flex items-center px-2 text-gray-600">
          {loading ? (
            <Loader2 className="w-4 h-4 text-landing-primary animate-spin" />
          ) : (
            <ChevronDown className="w-4 h-4 text-landing-primary/60" />
          )}
        </div>
        {hasError && (
          <p id={errorId} role="alert" className="text-xs font-semibold text-red-600">
            {errorMessage}
          </p>
        )}
      </div>
    );
  }
);

FetchDropdown.displayName = "FetchDropdown";
